import * as React from 'react'
import { useTranslation } from 'react-i18next'
import VisibilitySensor from 'react-visibility-sensor'

import {
  Basics,
  DirectorProfile,
  LicenseInformation,
  Location,
  RelatedPrograms,
} from 'main/components/school'
import AvailabilityBadge from 'main/components/school/AvailabilityBadge'
import { Section } from 'main/components/school/common'
import { SchoolListingType } from 'main/gql/globalTypes'
import { Hidden, palette, Visible } from 'main/ui'
import { LOCIZE_NAMESPACE } from '../../common'
import { i18nKeyPrefix } from './i18n'

import PhoneNumber from '../../components/school/PhoneNumber/PhoneNumber'
import Achievements, { hasAchievements } from './Achievements'
import CalendarTableSection, { IconKind } from './common/CalendarTableSection'
import RichTextSection from './common/RichTextSection'
import {
  AchievementsContainer,
  AvailabilityBadgeContainer,
  Container,
  CTASectionContainer,
  DirectorProfileContainer,
  LeftColumn,
  MobileColumn,
  PageContent,
  PhotosContainer,
  RatingsReviewsContainer,
  RightColumn,
} from './Content.styles'
import { CTABar, CTASection } from './CTA'
import DirectorEditBar from './DirectorEditBar'
import {
  SchoolPage_availabilitySurvey as AvailabilitySurvey,
  SchoolPage_school as School,
  SchoolPage_school_director as Director,
  SchoolPage_user as User,
} from './gql'
import Handbook from './Handbook'
import Hero from './Hero'
import { NetworkContactDetails } from './NetworkContactDetails'
import Photos from './Photos'
import { FullPricing } from './Pricing'
import RatingsReviews from './RatingsReviews'
import UpcomingGatherings from './UpcomingGatherings'
import { parseProfileContent } from './util/profileContent'

const PRE = `${i18nKeyPrefix}-CONTENT`

interface IProps {
  availabilitySurvey: AvailabilitySurvey | null
  director: Director
  school: School
  user: User | null
  onChangeCTASectionVisible: (ctaSectionVisible: boolean) => void
  eli: boolean
}

export default function Content(props: IProps) {
  const { availabilitySurvey, director, school, user, onChangeCTASectionVisible, eli } = props
  const { address, aggregateRatings, profile, upcomingGatherings } = school
  const profileContent = profile && parseProfileContent(profile)
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  return (
    <Container>
      {!!user && user.id === director.id && <DirectorEditBar schoolId={school.id} />}
      <Hidden lg xl>
        <CTABar school={school} />
      </Hidden>
      <Hero school={school} />
      <PageContent>
        <Visible lg xl>
          {renderDesktop()}
        </Visible>

        <Hidden lg xl>
          {renderMobile()}
        </Hidden>
      </PageContent>
    </Container>
  )

  function renderDesktop() {
    return (
      <>
        <LeftColumn>
          {renderDirectorProfileMaybe()}
          {renderAvailabilityBadgeMaybe('desktop')}
          {renderLicenseInformationSection()}
          {renderBasicsSection()}
          <div className="school-content">
            <style>{`
              .school-content img {
                max-width: 100%;
              }
            `}</style>
            {renderPhotosMaybe()}
            {renderTeachersSectionMaybe()}
            {renderAchievementsMaybe()}
            {renderRatingsReviews()}
            {renderTestimonialsSectionMaybe()}
            {renderPhilosophySectionMaybe()}
            {renderAboutSectionMaybe()}
            {renderRhythmMaybe()}
            {renderLocationSectionMaybe()}
            {renderCalendarMaybe()}
            {renderAdmissionsSectionMaybe()}
            {renderHandbookLinkMaybe()}
          </div>
        </LeftColumn>
        <RightColumn>
          {renderCTASection()}
          {renderSchoolPhoneMaybe()}
          {renderUpcomingGatheringsMaybe()}
          {renderLocationMaybe()}
          {renderRelatedPrograms()}
          <NetworkContactDetails school={school} align="left" />
        </RightColumn>
      </>
    )
  }

  function renderMobile() {
    return (
      <MobileColumn>
        {renderDirectorProfileMaybe()}
        {renderAvailabilityBadgeMaybe('mobile')}
        {renderSchoolPhoneMaybe()}
        {renderLocationMaybe()}
        {renderUpcomingGatheringsMaybe()}
        {school.licenseNumber && school.programType && renderLicenseInformationSection()}
        <NetworkContactDetails school={school} align="left" />
        {renderBasicsSection()}
        <div>
          {renderPhotosMaybe()}
          {renderTeachersSectionMaybe()}
          {renderAchievementsMaybe()}
          {renderRatingsReviews()}
          {renderTestimonialsSectionMaybe()}
          {renderPhilosophySectionMaybe()}
          {renderAboutSectionMaybe()}
          {renderRhythmMaybe()}
          {renderLocationSectionMaybe()}
          {renderCalendarMaybe()}
          {renderAdmissionsSectionMaybe()}
          {renderHandbookLinkMaybe()}
        </div>
      </MobileColumn>
    )
  }

  function renderDirectorProfileMaybe() {
    if (!director) return null

    return (
      <DirectorProfileContainer>
        <DirectorProfile
          avatarUrl={director.avatarUrl}
          aggregateRatings={aggregateRatings}
          firstName={director.firstName}
          lastName={director.lastName}
        />
      </DirectorProfileContainer>
    )
  }

  function renderAvailabilityBadgeMaybe(view: 'desktop' | 'mobile') {
    if (!availabilitySurvey) return null

    if (!availabilitySurvey.isOpen) return null
    if (availabilitySurvey.isStale) return null

    return (
      <AvailabilityBadgeContainer>
        <AvailabilityBadge
          compact={view === 'mobile'}
          availabilitySurvey={availabilitySurvey}
          eli={eli}
        />
      </AvailabilityBadgeContainer>
    )
  }

  function renderCTASection() {
    return (
      <VisibilitySensor onChange={onChangeCTASectionVisible} partialVisibility>
        <CTASectionContainer>
          <FullPricing school={school} />
          <CTASection
            full
            scheduleTourLabel={t(`${PRE}-CTA-SCHEDULE`)}
            messageDirectorLabel={t(`${PRE}-CTA-MESSAGE`)}
            school={school}
          />
        </CTASectionContainer>
      </VisibilitySensor>
    )
  }

  function renderSchoolPhoneMaybe() {
    if (!school.wonderschoolManagedPhoneVisible || !school.wonderschoolManagedPhone) return null

    return (
      <Section>
        <PhoneNumber phoneNumber={school.wonderschoolManagedPhone} />
      </Section>
    )
  }

  function renderLicenseInformationSection() {
    if (school.programType === 'other' || !school.programType) return null
    return (
      <Section>
        <LicenseInformation school={school} />
      </Section>
    )
  }

  function renderBasicsSection() {
    return (
      <Section>
        <Basics school={school} />
      </Section>
    )
  }

  function renderHandbookLinkMaybe() {
    if (!school.handbookPath) return null

    return (
      <Section>
        <Handbook path={school.handbookPath} />
      </Section>
    )
  }

  function renderPhotosMaybe() {
    if (!school.profile || !school.profile.profileMedia.length) return null

    return (
      <Section>
        <PhotosContainer>
          <Photos profileMedia={school.profile.profileMedia} />
        </PhotosContainer>
      </Section>
    )
  }

  function renderUpcomingGatheringsMaybe() {
    if (!upcomingGatherings.length) return null

    return (
      <Section>
        <UpcomingGatherings schoolId={school.id} upcomingGatherings={upcomingGatherings} />
      </Section>
    )
  }

  function renderLocationMaybe() {
    if (!address) return null

    return (
      <Section>
        <Location address={address} fuzzy={school.useFuzzyAddress} />
      </Section>
    )
  }

  function renderRelatedPrograms() {
    return (
      <Section>
        <RelatedPrograms school={school} type={SchoolListingType.INTERNAL} />
      </Section>
    )
  }

  function renderAchievementsMaybe() {
    const { achievements } = director
    if (!achievements || !hasAchievements(achievements)) return null

    return (
      <Section>
        <AchievementsContainer>
          <Achievements achievements={achievements} />
        </AchievementsContainer>
      </Section>
    )
  }

  function renderCalendarMaybe() {
    if (!profile || !profile.calendars) return null

    const calendar = profile.calendars[0]
    if (!calendar) return null

    const { entries, synopsis, title } = calendar
    if (!entries || !entries.length) return null

    return (
      <Section>
        <CalendarTableSection
          title={title || 'Calendar'}
          borderColor={palette.rose}
          color={palette.rose2}
          icon={IconKind.CALENDAR}
          entries={entries.map(entry => ({ when: entry.date, body: entry.reason }))}
          synopsis={synopsis}
        />
      </Section>
    )
  }

  function renderRhythmMaybe() {
    if (!profile || !profile.rhythm) return null

    const { beats, synopsis, title } = profile.rhythm
    if (!beats || !beats.length) return null

    return (
      <Section>
        <CalendarTableSection
          title={title || t(`${PRE}-RHYTHM-TITLE`)}
          borderColor={palette.fern}
          color={palette.fern2}
          icon={IconKind.CLOCK}
          entries={beats.map(item => ({ when: item.time, body: item.activity }))}
          synopsis={synopsis}
        />
      </Section>
    )
  }

  function renderRatingsReviews() {
    if (school.aggregateRatings.count <= 0) return null
    return (
      <Section>
        <RatingsReviewsContainer>
          <RatingsReviews aggregateRatings={school.aggregateRatings} reviews={school.reviews} />
        </RatingsReviewsContainer>
      </Section>
    )
  }

  function renderTeachersSectionMaybe() {
    if (!profileContent || !profileContent.teachers) return null

    return (
      <Section>
        <RichTextSection title={t(`${PRE}-TEACHERS-TITLE`)} content={profileContent.teachers} />
      </Section>
    )
  }

  function renderTestimonialsSectionMaybe() {
    if (!profileContent || !profileContent.testimonials) return null

    return (
      <Section>
        <RichTextSection
          title={t(`${PRE}-TESTIMONIALS-TITLE`)}
          content={profileContent.testimonials}
        />
      </Section>
    )
  }

  function renderPhilosophySectionMaybe() {
    if (!profileContent || !profileContent.philosophy) return null

    return (
      <Section>
        <RichTextSection title={t(`${PRE}-PHILOSOPHY-TITLE`)} content={profileContent.philosophy} />
      </Section>
    )
  }

  function renderAboutSectionMaybe() {
    if (!profileContent || !profileContent.about) return null

    return (
      <Section>
        <RichTextSection title={t(`${PRE}-ABOUT-TITLE`)} content={profileContent.about} />
      </Section>
    )
  }

  function renderLocationSectionMaybe() {
    if (!profileContent || !profileContent.location) return null

    return (
      <Section>
        <RichTextSection title={t(`${PRE}-LOCATION-TITLE`)} content={profileContent.location} />
      </Section>
    )
  }

  function renderAdmissionsSectionMaybe() {
    if (!profileContent || !profileContent.admissions) return null

    return (
      <Section>
        <RichTextSection title={t(`${PRE}-ADMISSIONS-TITLE`)} content={profileContent.admissions} />
      </Section>
    )
  }
}
